import React, { useState, ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronRight, ChevronDown, Menu, X, Baby } from 'lucide-react';

// Import shadcn components
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';

// Simplified types
export interface MenuItem {
    name: string;
    displayName: string;
    path: string;
    icon?: ReactNode;
    isOpen?: boolean;
    navigate?: 'header' | 'footer';
    order?: number;
    children?: MenuItem[];
    disabled?: boolean;
    className?: string;
    badge?: ReactNode;
}

export interface LayoutConfig {
    showHeader?: boolean;
    showFooter?: boolean;
    contentWidth?: 'full' | 'contained' | 'narrow';
    background?: 'default' | 'subtle' | 'transparent';
    logo?: ReactNode;
    containerClassName?: string;
    headerClassName?: string;
    footerClassName?: string;
    contentClassName?: string;
    headerExtraContent?: ReactNode;
    footerExtraContent?: ReactNode;
}

interface MainLayoutProps {
    children: ReactNode;
    menu: MenuItem[];
    config?: LayoutConfig;
}

interface NavigationItemProps {
    item: MenuItem;
    onClick?: () => void;
    className?: string;
    showIcons?: boolean;
    variant?: 'default' | 'header' | 'footer';
    isCollapsible?: boolean;
}

// Navigation item component
const NavigationItem: React.FC<NavigationItemProps> = ({
    item,
    onClick,
    className,
    showIcons = true,
    variant = 'default',
    isCollapsible = true
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const hasChildren = item.children?.length ?? 0 > 0;
    const hasDisabledClass = item.disabled ? 'opacity-50 cursor-not-allowed' : '';

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (hasChildren && isCollapsible) {
            e.preventDefault();
            setIsOpen(!isOpen);
        } else if (onClick) {
            onClick();
        }
    };

    const variantStyles = {
        default: 'text-foreground hover:text-primary font-medium',
        header: 'text-header-foreground hover:bg-primary/15 hover:text-primary rounded-md font-medium',
        footer: 'text-footer-foreground hover:text-primary font-medium'
    };

    const LinkContent = (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
                {showIcons && item.icon && (
                    <span className="flex-shrink-0">{item.icon}</span>
                )}
                <span>{item.displayName}</span>
            </div>

            <div className="flex items-center gap-2">
                {item.badge && (
                    <span className="flex-shrink-0">{item.badge}</span>
                )}
                {hasChildren && isCollapsible && variant === 'header' && (
                    <span className="text-muted-foreground">
                        <ChevronDown className="h-4 w-4" />
                    </span>
                )}
                {hasChildren && isCollapsible && variant !== 'header' && (
                    <span className="text-muted-foreground">
                        {isOpen ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </span>
                )}
            </div>
        </div>
    );

    // Special handling for header navigation with dropdown (mobile-friendly)
    if (hasChildren && isCollapsible && variant === 'header') {
        // Check if we're in the mobile view (Sheet component)
        const isMobileView = className?.includes('-mx-1');

        // For mobile view, we use a different approach - no dropdown, just expand in place
        if (isMobileView) {
            return (
                <div className="w-full">
                    <Link
                        to={item.path}
                        className={cn(
                            "flex py-2 px-3",
                            variantStyles[variant],
                            hasDisabledClass,
                            className
                        )}
                        onClick={handleClick}
                        role="menuitem"
                        aria-disabled={item.disabled}
                    >
                        {LinkContent}
                    </Link>

                    {isOpen && (
                        <div className="pl-4 mt-1 border-l-2 border-primary/20 space-y-1">
                            {item.children?.map((child) => (
                                <Link
                                    key={child.name}
                                    to={child.path}
                                    className={cn(
                                        "flex py-2 px-4 text-sm hover:bg-primary/15 hover:text-primary",
                                        "text-header-foreground font-medium",
                                        child.disabled ? 'opacity-50 cursor-not-allowed' : ''
                                    )}
                                    onClick={() => {
                                        if (onClick) onClick();
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        {showIcons && child.icon && (
                                            <span className="flex-shrink-0">{child.icon}</span>
                                        )}
                                        <span>{child.displayName}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        // For desktop, we use the dropdown approach
        return (
            <div className="relative" ref={dropdownRef}>
                <Link
                    to={item.path}
                    className={cn(
                        "flex py-2 px-3",
                        variantStyles[variant],
                        hasDisabledClass,
                        className
                    )}
                    onClick={handleClick}
                    role="menuitem"
                    aria-disabled={item.disabled}
                >
                    {LinkContent}
                </Link>

                {isOpen && (
                    <div className="absolute left-0 top-full mt-1 w-56 rounded-md shadow-lg bg-white z-30 border border-border overflow-hidden">
                        <div className="py-1">
                            {item.children?.map((child) => (
                                <Link
                                    key={child.name}
                                    to={child.path}
                                    className={cn(
                                        "flex py-2 px-4 text-sm hover:bg-primary/15 hover:text-primary",
                                        "text-header-foreground font-medium",
                                        child.disabled ? 'opacity-50 cursor-not-allowed' : ''
                                    )}
                                    onClick={() => {
                                        setIsOpen(false);
                                        if (onClick) onClick();
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        {showIcons && child.icon && (
                                            <span className="flex-shrink-0">{child.icon}</span>
                                        )}
                                        <span>{child.displayName}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Standard collapsible for sidebar and other non-header items
    if (hasChildren && isCollapsible) {
        return (
            <div className="w-full">
                <Collapsible open={isOpen} onOpenChange={setIsOpen} className="w-full">
                    <CollapsibleTrigger asChild className="w-full">
                        <Link
                            to={item.path}
                            className={cn(
                                "flex py-2 px-3",
                                variantStyles[variant],
                                hasDisabledClass,
                                className
                            )}
                            onClick={handleClick}
                            role="menuitem"
                            aria-disabled={item.disabled}
                        >
                            {LinkContent}
                        </Link>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="pl-6 mt-1 space-y-1 border-l border-border">
                            {item.children?.map((child) => (
                                <NavigationItem
                                    key={child.name}
                                    item={child}
                                    onClick={onClick}
                                    className={className}
                                    showIcons={showIcons}
                                    variant={variant}
                                />
                            ))}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            </div>
        );
    }

    // Regular link with no children
    return (
        <Link
            to={item.path}
            className={cn(
                "flex py-2 px-3",
                variantStyles[variant],
                hasDisabledClass,
                className
            )}
            onClick={handleClick}
            role="menuitem"
            aria-disabled={item.disabled}
        >
            {LinkContent}
        </Link>
    );
};

interface HeaderProps {
    menu: MenuItem[];
    logo?: ReactNode;
    extraContent?: ReactNode;
    className?: string;
}

// Header component
const Header: React.FC<HeaderProps> = ({ menu, logo, extraContent, className }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    // Filter header menu items
    const headerItems = React.useMemo(() => {
        return menu
            .filter(item => item.navigate === 'header')
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [menu]);

    return (
        <header className={cn(
            "w-full py-2 sm:py-3 px-3 sm:px-4 bg-white shadow-lg border-b border-border",
            className
        )}>
            <div className="container mx-auto flex items-center relative">
                {/* Logo area - simplified on mobile */}
                <Link to="/" className="shrink-0">
                    {logo || (
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-primary/10 rounded-full">
                                <Baby className="h-6 w-6 text-primary" />
                            </div>
                            <span className="font-bold text-xl text-header-foreground">
                                Berry Babies
                            </span>
                        </div>
                    )}
                </Link>

                {/* Desktop navigation - centered */}
                <nav className="hidden md:flex items-center gap-1 absolute left-1/2 transform -translate-x-1/2">
                    {headerItems.map((item) => (
                        <NavigationItem
                            key={item.name}
                            item={item}
                            variant="header"
                            className="nav-baby"
                        />
                    ))}
                </nav>

                {/* Extra content and mobile menu button */}
                <div className="flex items-center gap-1 sm:gap-2 ml-auto">
                    {/* Render a simplified version of extraContent on mobile if it exists */}
                    {extraContent}

                    <Button
                        variant="ghost"
                        size="icon"
                        className="md:hidden text-berry-purple hover:bg-berry-purple/10"
                        onClick={() => setIsOpen(true)}
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </Button>
                </div>

                {/* Completely redesigned mobile navigation */}
                <Sheet open={isOpen} onOpenChange={setIsOpen}>
                    <SheetContent
                        side="right"
                        className="p-0 w-full sm:w-[380px] bg-card border-l border-border overflow-hidden"
                        showCloseButton={false}
                    >
                        {/* Custom mobile navigation header with improved visuals */}
                        <div className="relative h-24 bg-gradient-to-r from-berry-purple to-berry-indigo flex items-end px-5 pb-4">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-2">
                                    <div className="bg-white p-2 rounded-full shadow-md">
                                        <Baby className="h-5 w-5 text-berry-purple" />
                                    </div>
                                    <span className="text-lg font-bold text-white">Berry Babies</span>
                                </div>
                                <SheetClose asChild>
                                    <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 rounded-full">
                                        <X className="h-4 w-4" />
                                    </Button>
                                </SheetClose>
                            </div>
                        </div>

                        {/* Custom navigation menu with improved distinction */}
                        <div className="h-[calc(100vh-96px)] overflow-auto">
                            <div className="p-4 space-y-2">
                                {headerItems.map((item) => (
                                    <div key={item.name} className="mb-3 last:mb-0">
                                        <Link
                                            to={item.path}
                                            className="flex items-center py-3 px-4 rounded-xl bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all border border-border"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <div className="bg-primary/10 w-10 h-10 rounded-xl flex items-center justify-center mr-3 shadow-inner">
                                                {item.icon ||
                                                    <span className="font-bold text-primary text-lg">{item.displayName.charAt(0)}</span>
                                                }
                                            </div>
                                            <span className="font-semibold text-foreground">{item.displayName}</span>
                                            {item.children && item.children.length > 0 && (
                                                <ChevronRight className="h-4 w-4 ml-auto text-primary/70" />
                                            )}
                                        </Link>

                                        {/* If this item has children and is open, show them with indentation and styling */}
                                        {item.children && item.isOpen && (
                                            <div className="pl-8 mt-2 space-y-2">
                                                {item.children.map(child => (
                                                    <Link
                                                        key={child.name}
                                                        to={child.path}
                                                        className="flex items-center py-2 px-4 rounded-lg bg-background dark:bg-gray-700 border border-border/50"
                                                        onClick={() => setIsOpen(false)}
                                                    >
                                                        <div className="w-7 h-7 rounded-lg bg-primary/5 flex items-center justify-center mr-3">
                                                            {child.icon ||
                                                                <span className="font-medium text-primary/70 text-sm">{child.displayName.charAt(0)}</span>
                                                            }
                                                        </div>
                                                        <span className="font-medium text-foreground/90 text-sm">{child.displayName}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Custom CTA section with improved visual appeal */}
                            <div className="p-4 mt-2 border-t border-border">
                                <div className="p-5 mb-5 rounded-2xl bg-gradient-to-br from-berry-blue/20 to-berry-indigo/20 border border-primary/20 shadow-sm">
                                    <h3 className="font-bold text-foreground text-lg mb-1">Try Berry Sensors</h3>
                                    <p className="text-foreground/70 text-sm mb-4">Get real-time baby monitoring with smart sensors.</p>
                                    <Button className="w-full bg-berry-purple hover:bg-berry-indigo text-white rounded-xl py-2 shadow-md hover:shadow-lg transition-all">
                                        Get Started
                                    </Button>
                                </div>

                                <div className="flex items-center justify-between gap-3">
                                    <Button variant="outline" className="flex-1 py-2 border-2 border-berry-indigo/30 text-berry-indigo dark:text-berry-blue rounded-xl font-semibold">
                                        Log In
                                    </Button>
                                    <Button className="flex-1 py-2 bg-berry-indigo hover:bg-berry-purple text-white rounded-xl font-semibold shadow-sm hover:shadow-md transition-all">
                                        Sign Up
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </header>
    );
};

interface FooterProps {
    menu: MenuItem[];
    extraContent?: ReactNode;
    className?: string;
}

// Footer component
const Footer: React.FC<FooterProps> = ({ menu, extraContent, className }) => {
    // Filter footer menu items
    const footerItems = React.useMemo(() => {
        return menu
            .filter(item => item.navigate === 'footer')
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [menu]);

    return (
        <footer className={cn(
            "z-100 bg-white dark:bg-gray-900 py-8 px-4 text-gray-700 dark:text-gray-300 mt-0 border-t border-gray-200 dark:border-gray-800 shadow-[0_-5px_30px_rgba(0,0,0,0.05)]",
            className
        )}>
            <div className="container mx-auto flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
                <div className="text-center md:text-left mb-4 md:mb-0">
                    <Link to="/" className="flex items-center justify-center md:justify-start gap-2 mb-3">
                        <div className="p-2 bg-blue-500/10 rounded-full">
                            <Baby className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <span className="font-bold text-base sm:text-lg text-gray-800 dark:text-white">Berry Babies</span>
                    </Link>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                        &copy; {new Date().getFullYear()} Berry Babies Smart Diapers. All rights reserved.
                    </p>
                </div>

                <nav className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-4 md:mb-0 order-3 md:order-2">
                    {footerItems.map((item) => (
                        <NavigationItem
                            key={item.name}
                            item={item}
                            variant="footer"
                            className="text-xs sm:text-sm py-1"
                            showIcons={true}
                            isCollapsible={false}
                        />
                    ))}
                </nav>

                {extraContent && (
                    <div className="order-2 md:order-3">{extraContent}</div>
                )}
            </div>

            {/* Mobile-friendly bottom bar for small screens */}
            <div className="md:hidden text-center text-xs text-gray-500 dark:text-gray-500 mt-6 pt-3 border-t border-gray-200 dark:border-gray-800">
                <p>Made with ❤️ for babies everywhere</p>
            </div>
        </footer>
    );
};

// Main layout component - Simplified
export const MainLayout: React.FC<MainLayoutProps> = ({ children, menu, config = {} }) => {
    const {
        showHeader = true,
        showFooter = true,
        contentWidth = 'contained',
        background = 'default',
        logo,
        containerClassName,
        headerClassName,
        footerClassName,
        contentClassName,
        headerExtraContent,
        footerExtraContent,
    } = config;

    const backgroundClasses: Record<string, string> = {
        default: 'bg-background',
        subtle: 'bg-muted/30',
        transparent: 'bg-transparent',
    };

    const contentWidthClasses: Record<string, string> = {
        full: 'w-full',
        contained: 'container mx-auto px-4 max-w-full box-border',
        narrow: 'container mx-auto px-4 max-w-3xl box-border',
    };

    return (
        <div className={cn(
            "flex flex-col min-h-screen",
            backgroundClasses[background],
            containerClassName
        )}>
            {showHeader && (
                <div className="fixed top-0 left-0 right-0 z-50">
                    <Header
                        menu={menu}
                        logo={logo}
                        extraContent={headerExtraContent}
                        className={headerClassName}
                    />
                </div>
            )}

            <main className={cn(
                "flex-grow w-full",
                contentWidthClasses[contentWidth],
                contentClassName,
                showHeader && "pt-16 sm:pt-20" // Add padding top to account for fixed header
            )}>
                {children}
            </main>

            {showFooter && (
                <Footer
                    menu={menu}
                    extraContent={footerExtraContent}
                    className={footerClassName}
                />
            )}
        </div>
    );
};